import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Heart, Instagram, Facebook, Twitter, Mail, MapPin, Phone } from 'lucide-react';
import { colors, spacing, typography, shadows, transitions, easings } from '../../styles/designSystem';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, ${colors.sacred.whisper} 0%, ${colors.sacred.cream} 100%);
  border-top: 1px solid ${colors.sacred.mist};
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${spacing[16]} ${spacing[4]} ${spacing[8]};
  
  @media (min-width: 640px) {
    padding: ${spacing[20]} ${spacing[8]} ${spacing[12]};
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[12]};
  margin-bottom: ${spacing[12]};
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: ${spacing[8]};
  }
`;

const FooterSection = styled.div`
  text-align: center;
  
  @media (min-width: 768px) {
    text-align: left;
  }
`;

const BrandSection = styled(FooterSection)`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    align-items: flex-start;
  }
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing[4]};
  cursor: pointer;
`;

const LogoImage = styled.img`
  height: 48px;
  width: auto;
  margin-right: ${spacing[3]};
`;

const LogoText = styled.span`
  font-family: ${typography.fonts.display};
  font-size: ${typography.sizes['2xl']};
  font-weight: ${typography.weights.bold};
  color: ${colors.sacred.night};
`;

const Tagline = styled.p`
  font-style: italic;
  color: ${colors.sacred.shadow};
  margin-bottom: ${spacing[6]};
  font-size: ${typography.sizes.lg};
  line-height: ${typography.lineHeights.relaxed};
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  max-width: 300px;
  width: 100%;
  
  @media (min-width: 768px) {
    max-width: none;
  }
`;

const NewsletterInput = styled.input`
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.sacred.stone};
  border-radius: ${spacing[3]};
  font-size: ${typography.sizes.sm};
  background: ${colors.sacred.white};
  transition: ${transitions.gentle};
  
  &:focus {
    border-color: ${colors.tiffany[400]};
    box-shadow: 0 0 0 3px ${colors.tiffany[200]}40;
  }
  
  &::placeholder {
    color: ${colors.sacred.sage};
  }
`;

const NewsletterButton = styled(motion.button)`
  padding: ${spacing[3]} ${spacing[6]};
  background: linear-gradient(135deg, ${colors.tiffany[500]}, ${colors.tiffany[600]});
  color: ${colors.sacred.white};
  border: none;
  border-radius: ${spacing[3]};
  font-weight: ${typography.weights.medium};
  cursor: pointer;
  transition: ${transitions.gentle};
  box-shadow: 0 4px 12px ${colors.tiffany[400]}30;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: ${transitions.flowing};
  }
  
  &:hover {
    background: linear-gradient(135deg, ${colors.tiffany[600]}, ${colors.tiffany[700]});
    box-shadow: 0 6px 20px ${colors.tiffany[400]}40;
    
    &::before {
      left: 100%;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    &::before {
      display: none;
    }
  }
`;

const SectionTitle = styled.h4`
  font-family: ${typography.fonts.display};
  font-size: ${typography.sizes.lg};
  font-weight: ${typography.weights.semibold};
  color: ${colors.sacred.night};
  margin-bottom: ${spacing[4]};
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: ${spacing[2]};
`;

const FooterLink = styled(Link)`
  color: ${colors.sacred.shadow};
  text-decoration: none;
  font-size: ${typography.sizes.sm};
  transition: ${transitions.gentle};
  
  &:hover {
    color: ${colors.tiffany[600]};
    text-decoration: underline;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${spacing[4]};
  justify-content: center;
  
  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${colors.sacred.white};
  border: 1px solid ${colors.sacred.stone};
  border-radius: 50%;
  color: ${colors.sacred.shadow};
  text-decoration: none;
  transition: ${transitions.gentle};
  
  &:hover {
    background: ${colors.tiffany[50]};
    border-color: ${colors.tiffany[300]};
    color: ${colors.tiffany[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px ${colors.tiffany[300]}30;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  color: ${colors.sacred.shadow};
  font-size: ${typography.sizes.sm};
  justify-content: center;
  
  svg {
    color: ${colors.tiffany[500]};
    flex-shrink: 0;
  }
  
  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${colors.sacred.mist};
  padding-top: ${spacing[8]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[4]};
  text-align: center;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
`;

const Copyright = styled.p`
  color: ${colors.sacred.sage};
  font-size: ${typography.sizes.sm};
  margin: 0;
`;

const MadeWith = styled.p`
  color: ${colors.sacred.sage};
  font-size: ${typography.sizes.sm};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const HeartIcon = styled(motion.span)`
  color: ${colors.tiffany[500]};
`;

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      alert('🌙 Please enter a valid email address for your sacred wellness journey! 🌙');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source: 'website_footer',
          interests: ['general_wellness']
        }),
      });

      if (!response.ok) {
        throw new Error('Subscription failed');
      }

      const result = await response.json();
      console.log('✅ Newsletter subscription successful:', result);
      
      // Show success (you could add a toast notification here)
      alert('🌙✨ Thank you for subscribing to our sacred wellness wisdom! ✨🌙');
      setEmail('');
      
    } catch (error) {
      console.error('❌ Newsletter subscription error:', error);
      alert('💫 Sacred energies are temporarily disrupted. Please try again! 💫');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: Instagram, url: 'https://instagram.com/ama_spa_caledonia', label: 'Instagram' },
    { icon: Facebook, url: 'https://www.facebook.com/profile.php?id=100091691056629', label: 'Facebook' }
    // { icon: Twitter, url: '#', label: 'Twitter' }
  ];

  const quickLinks = [
    { to: '/treatments', label: t('footer.treatments') },
    { to: '/wellness-quiz', label: t('nav.wellnessQuiz') },
    { to: '/journal', label: t('nav.blog') },
    { to: '/contact', label: t('nav.contact') }
  ];

  const policyLinks = [
    { to: '/privacy', label: t('footer.privacy') },
    { to: '/terms', label: t('footer.terms') }
  ];

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <BrandSection>
            <LogoContainer
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2, ease: easings.gentle }}
            >
              {/* <LogoImage src="/images/logo.jpg" alt="Ama Wellness & Spa Logo" /> */}
              <LogoText>Ama Wellness & Spa</LogoText>
            </LogoContainer>
            
            <Tagline>{t('footer.tagline')}</Tagline>
            
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <SectionTitle>{t('footer.newsletter')}</SectionTitle>
              <p style={{ 
                color: colors.sacred.shadow, 
                fontSize: typography.sizes.sm,
                marginBottom: spacing[3]
              }}>
                {t('footer.newsletterDesc')}
              </p>
              <NewsletterInput
                type="email"
                placeholder={t('common.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <NewsletterButton
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? t('common.loading') : t('common.subscribe')}
              </NewsletterButton>
            </NewsletterForm>
            
            <SocialLinks style={{ marginTop: spacing[6] }}>
              {socialLinks.map((social, index) => (
                <SocialLink
                  key={index}
                  href={social.url}
                  aria-label={social.label}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon size={18} />
                </SocialLink>
              ))}
            </SocialLinks>
          </BrandSection>

          <FooterSection>
            <SectionTitle>{t('footer.quickLinks')}</SectionTitle>
            <LinkList>
              {quickLinks.map((link) => (
                <LinkItem key={link.to}>
                  <FooterLink to={link.to}>{link.label}</FooterLink>
                </LinkItem>
              ))}
            </LinkList>
          </FooterSection>

          <FooterSection>
            <SectionTitle>{t('footer.policies')}</SectionTitle>
            <LinkList>
              {policyLinks.map((link) => (
                <LinkItem key={link.to}>
                  <FooterLink to={link.to}>{link.label}</FooterLink>
                </LinkItem>
              ))}
            </LinkList>
          </FooterSection>

          <FooterSection>
            <SectionTitle>{t('nav.contact')}</SectionTitle>
            <ContactInfo>
              <ContactItem>
                <MapPin size={16} />
                <span>Urbanización Bel Air<br />Carretera Cadiz 340, C. Hinojo, S/N<br />km 166, Marbella</span>
              </ContactItem>
              <ContactItem>
                <Phone size={16} />
                <span>+34 610 76 02 78</span>
              </ContactItem>
              <ContactItem>
                <Mail size={16} />
                <span>contact@amawellness.es</span>
              </ContactItem>
            </ContactInfo>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <Copyright>{t('footer.copyright')}</Copyright>
          <MadeWith>
            Made with{' '}
            <HeartIcon
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, ease: easings.breath }}
            >
              <Heart size={14} fill="currentColor" />
            </HeartIcon>
            {' '} by <a href="https://emerv.com" target="_blank" rel="noopener noreferrer">EmerV</a>🌸 @{' '}
            <a 
              href="https://infinia.ie" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >Infinia
            </a>
          </MadeWith>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 
